/**
 * WS4 Captures modal — gallery of whiteboard photos and screenshots
 * auto-saved from the WS4 session, plus user-added captures (HAH/STR
 * snapshot PNGs and XAES Analyst chat exports). Renders as a 3-col
 * grid of capture cards; clicking a card opens a small in-modal
 * lightbox showing the real payload (image / markdown text) for
 * blob-backed captures, or the decorative thumbnail + caption for
 * seed entries.
 *
 * Ported from:
 *   - `_legacy/ws4-assetlocker.js` lines 994-1283 — `#capturesModal`
 *     shell: `modal-header` (PHOTO_LIBRARY icon + "CAPTURES" title +
 *     ASSET LOCKER pill + WS4 pill + "12 CAPTURES · 14 MAR 2026"
 *     indicator + ✕ CLOSE), and the two `.cap-grid` blocks
 *     (`cap-pane-boards` and `cap-pane-screenshots`) with their
 *     `.cap-card` + `.cap-thumb` + `.cap-body` rows. The two vanilla
 *     panes are collapsed into a single ordered grid in the React
 *     port; the WHITEBOARD/SCREEN distinction survives as a per-card
 *     tag derived from `kind`.
 *
 * Visual conventions match `PlaybackModal` / `XaesAgentModal`:
 * `PanelDialogContent` shell, true-blue border + halo, brushed grey
 * outline, 12 px radius, 3-col title bar (left identity / centre
 * indicator / right ✕ Close), and the workshop footer bar pinned to
 * the bottom.
 *
 * User captures (HAH/STR snapshots + chat exports) are merged in
 * from `useCaptures` (see `stores/useCaptures.ts`) — newest first,
 * prepended to the decorative seed entries from
 * `captures-content.ts`. Blob-backed image captures render the real
 * PNG inside `CaptureThumb`; markdown chat exports show their text
 * in the lightbox body, matching the `UploadLightbox` chrome from
 * the legacy `AssetLocker` preview path.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Clock, Images, RotateCcw, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { can } from "@/lib/auth/can";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { loadBlob } from "@/lib/blob-store";
import type { Workshop } from "@/content/workshops";
import { isVersionHistoryEnabled } from "@/env";
import { TagInput } from "@/components/tags/TagInput";
import { useWorkshopModals } from "@/features/workshop/modals/useWorkshopModals";
import { useVersionHistory } from "@/features/versioning/useVersionHistory";
import { useJumpTarget } from "@/features/search/useJumpTarget";
import { useCaptures, type UserCapture } from "@/stores/useCaptures";
import { withSessionDate } from "@/features/workshop/session-date";
import {
  getCaptures,
  type CaptureAccent,
  type CaptureCard,
  type CaptureKind,
} from "./captures-content";

type CapturesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

/* ------------------------ Merged capture type ------------------------ */

/**
 * Unified card type rendered by the grid + lightbox. Seed entries
 * (from `captures-content.ts`) carry no payload — they render the
 * decorative thumb. User entries (from `useCaptures`) may carry a
 * `blobKey` (snapshot PNGs) or a `url` (markdown data-URL chat
 * exports). `addedAt` is undefined for seed entries — they precede
 * the live-system epoch so we order them after user-added.
 */
type GridCapture = {
  id: string;
  title: string;
  activity: string;
  timestamp: string;
  /** Narrowed accent — the `useCaptures.UserCapture` accent set is a
   *  superset of the seed-content accent set, but every value lives in
   *  the seed `CaptureAccent` union so we can render uniformly. */
  accent: CaptureAccent;
  /** Glyph selector. `whiteboard` / `screen` drive the seed glyph;
   *  `snapshot` falls back to whiteboard, `chat` to screen. */
  kind: "whiteboard" | "screen" | "snapshot" | "chat";
  source: "Saved" | "Auto-captured";
  caption: string;
  blobKey?: string;
  mimeType?: string;
  url?: string;
  /** GitHub mirror fields (synced user captures only) — drive the
   *  version-history Clock affordance. RT-VERSIONING. */
  githubPath?: string;
  githubSha?: string;
  /** Sort key. Seed entries get `0` so they trail user-added (which
   *  use `addedAt` epoch ms). */
  addedAt: number;
  /** `"seed"` for decorative cards; the user's name for user-added.
   *  Drives the × remove button on the grid card. */
  addedBy: string;
};

function seedToGrid(card: CaptureCard): GridCapture {
  return {
    id: card.id,
    title: card.title,
    activity: card.activity,
    timestamp: card.timestamp,
    accent: card.accent,
    kind: card.kind,
    source: card.source,
    caption: card.caption,
    addedAt: 0,
    addedBy: "seed",
  };
}

function userToGrid(capture: UserCapture): GridCapture {
  // The `UserCapture.accent` set is a superset; clamp to the seed
  // union for the renderer. Every emitted accent today lives in the
  // seed set so this is currently a no-op cast, kept explicit so a
  // future store-side accent doesn't break the grid.
  const accent: CaptureAccent =
    capture.accent === "true-blue" ||
    capture.accent === "sky" ||
    capture.accent === "gold" ||
    capture.accent === "green" ||
    capture.accent === "melon" ||
    capture.accent === "red"
      ? capture.accent
      : "true-blue";
  return {
    id: capture.id,
    title: capture.title,
    activity: capture.activity,
    timestamp: capture.timestamp,
    accent,
    kind: capture.kind,
    source: capture.source,
    caption: capture.caption,
    blobKey: capture.blobKey,
    mimeType: capture.mimeType,
    url: capture.url,
    githubPath: capture.githubPath,
    githubSha: capture.githubSha,
    addedAt: capture.addedAt,
    addedBy: capture.addedBy,
  };
}

/* ------------------------- Accent → token ------------------------- */

const ACCENT_RGB: Record<CaptureAccent, string> = {
  "true-blue": "var(--accent-true-blue-rgb)",
  sky: "var(--accent-sky-rgb)",
  gold: "var(--accent-gold-rgb)",
  green: "var(--accent-green-rgb)",
  melon: "var(--accent-melon-rgb)",
  red: "var(--accent-red-rgb)",
};

const ACCENT_VAR: Record<CaptureAccent, string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  gold: "var(--accent-gold)",
  green: "var(--accent-green)",
  melon: "var(--accent-melon)",
  red: "var(--accent-red)",
};

const KIND_LABEL: Record<GridCapture["kind"], string> = {
  whiteboard: "Whiteboard",
  screen: "Screen",
  snapshot: "Snapshot",
  chat: "Chat",
};

/** Glyph fallback. `snapshot` reads as a whiteboard photo, `chat` as
 *  a screen-style export — keeps the binary glyph split that the
 *  seed renderer already uses. */
function glyphFor(kind: GridCapture["kind"]): CaptureKind {
  if (kind === "whiteboard" || kind === "snapshot") return "whiteboard";
  return "screen";
}

/** Stable empty-array fallback for the zustand selector so an empty
 *  workshop bucket doesn't churn a new reference each render. Mirrors
 *  the `EMPTY_UPLOADS` posture in `AssetLocker.tsx`. */
const EMPTY_CAPTURES: readonly UserCapture[] = Object.freeze([]);
const EMPTY_HIDDEN_SEEDS: readonly string[] = Object.freeze([]);

/* --------------------------- Thumbnail --------------------------- */

/**
 * Decorative thumbnail block — we don't ship real images for seed
 * entries, so render a tinted gradient + glyph keyed to the
 * capture's accent. Mirrors the `.cap-thumb-overlay` radial-gradient
 * pattern from the vanilla whiteboard cards
 * (`_legacy/ws4-assetlocker.js` lines 1044-1057).
 *
 * When `imageUrl` is supplied (blob-backed user captures, MIME
 * `image/*`) we render the real PNG with `object-fit: cover` so the
 * grid card reads as a true preview, falling back to the decorative
 * gradient on hover/loading-error edge cases.
 */
function CaptureThumb({
  capture,
  enlarged = false,
  imageUrl,
}: {
  capture: GridCapture;
  enlarged?: boolean;
  imageUrl?: string;
}) {
  const accentRgb = ACCENT_RGB[capture.accent];
  const accentVar = ACCENT_VAR[capture.accent];
  const Glyph = glyphFor(capture.kind) === "whiteboard" ? Images : Camera;
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        // Real previews size the box to the image (no letterbox bands);
        // only the decorative placeholder keeps the fixed 4:3 block.
        ...(imageUrl ? {} : { aspectRatio: "4 / 3" }),
        background: "var(--bg-deep)",
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
      }}
    >
      {imageUrl ? (
        // Real image preview (user-added snapshot PNG) at its natural
        // aspect — the tile hugs the image, full frame always visible.
        <img
          src={imageUrl}
          alt={capture.title}
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      ) : (
        <>
          {/* Tinted radial wash */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(ellipse at 40% 45%, rgba(${accentRgb}, 0.18), transparent 65%), radial-gradient(ellipse at 70% 70%, rgba(${accentRgb}, 0.08), transparent 60%)`,
            }}
          />
          {/* Faint grid texture so the block doesn't read as a solid colour */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(rgba(${accentRgb}, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(${accentRgb}, 0.04) 1px, transparent 1px)`,
              backgroundSize: enlarged ? "48px 48px" : "32px 32px",
              opacity: 0.7,
            }}
          />
          {/* Centred placeholder glyph */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <Glyph
              style={{
                width: enlarged ? 56 : 30,
                height: enlarged ? 56 : 30,
                color: accentVar,
                opacity: 0.55,
              }}
            />
          </div>
        </>
      )}
      {/* Kind tag (top-left) — always rendered, including over real
          imagery, so the WHITEBOARD/SCREEN/SNAPSHOT/CHAT split stays
          legible at a glance. */}
      <span
        className="font-mono absolute font-semibold tracking-wider uppercase"
        style={{
          top: 8,
          left: 8,
          padding: "2px 7px",
          borderRadius: 6,
          fontSize: "var(--fs-micro)",
          background: `rgba(${accentRgb}, 0.15)`,
          color: accentVar,
          border: `1px solid rgba(${accentRgb}, 0.3)`,
        }}
      >
        {KIND_LABEL[capture.kind]}
      </span>
      {/* Timestamp (bottom-right) */}
      <span
        className="font-mono absolute tracking-wider"
        style={{
          bottom: 8,
          right: 8,
          padding: "1px 6px",
          borderRadius: 4,
          fontSize: "var(--fs-micro)",
          background: "rgba(0,0,0,0.55)",
          color: "var(--accent-sky)",
        }}
      >
        {capture.timestamp}
      </span>
    </div>
  );
}

/* ---------------------------- Card ------------------------------- */

function CaptureGridCard({
  capture,
  imageUrl,
  onSelect,
  onRemove,
  onHistory,
}: {
  capture: GridCapture;
  imageUrl?: string;
  onSelect: () => void;
  onRemove?: () => void;
  /** Open version history — present only for synced captures. RT-VERSIONING. */
  onHistory?: () => void;
}) {
  const [hover, setHover] = useState(false);
  const removable = onRemove !== undefined;
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        onClick={onSelect}
        className="group flex w-full flex-col overflow-hidden text-left transition-all"
        style={{
          background: "var(--bg-card)",
          border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
          borderRadius: 10,
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor =
            "rgba(var(--accent-true-blue-rgb), 0.3)";
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow =
            "0 4px 20px rgba(var(--accent-true-blue-rgb), 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            "rgba(var(--accent-true-blue-rgb), 0.12)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <CaptureThumb capture={capture} imageUrl={imageUrl} />
        <div className="flex flex-col gap-1 px-3 py-2.5">
          <div
            className="truncate font-semibold"
            style={{
              color: "var(--text-primary)",
              fontSize: "var(--fs-base)",
            }}
          >
            {capture.title}
          </div>
          {/* Caption preview — for chat captures this is the AI summary of
              what was discussed (see `chat-summary.ts`); for other kinds
              it's the authored one-liner. Clamped to two lines so the grid
              stays even. */}
          {capture.caption && (
            <p
              className="line-clamp-2 leading-snug"
              style={{
                color: "var(--text-secondary)",
                fontSize: "var(--fs-label)",
                margin: 0,
              }}
            >
              {capture.caption}
            </p>
          )}
          <div
            className="font-mono flex items-center gap-1.5 tracking-wider"
            style={{
              color: "var(--text-muted)",
              fontSize: "var(--fs-micro)",
            }}
          >
            <span>{capture.activity}</span>
            <span aria-hidden>·</span>
            <span>{capture.timestamp}</span>
            <span aria-hidden>·</span>
            <span
              style={{
                color:
                  capture.source === "Saved"
                    ? "var(--accent-green)"
                    : "var(--accent-sky)",
              }}
            >
              {capture.source}
            </span>
          </div>
        </div>
      </button>
      {/* Clock on hover — opens version history. Only for synced
          captures (a githubPath exists). Mirrors UploadCard. RT-VERSIONING. */}
      {onHistory && hover && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onHistory();
          }}
          aria-label={`Version history for ${capture.title}`}
          title="Version history"
          style={{
            position: "absolute",
            top: 6,
            right: 32,
            width: 20,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.65)",
            color: "var(--text-muted)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
            borderRadius: 4,
            cursor: "pointer",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--accent-true-blue)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--text-muted)";
          }}
        >
          <Clock aria-hidden style={{ width: 12, height: 12 }} />
        </button>
      )}
      {/* × on hover — removes the user-added capture + sweeps its
          blob (the store's `remove` already calls `deleteBlob`).
          Hidden for seed entries (no `onRemove` supplied). Matches
          `UploadCard` × pattern in `AssetLocker.tsx`. */}
      {removable && hover && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${capture.title}`}
          title="Remove"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 20,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.65)",
            color: "var(--text-muted)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
            borderRadius: 4,
            cursor: "pointer",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--accent-red)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color =
              "var(--text-muted)";
          }}
        >
          <X aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

/* --------------------------- Lightbox ---------------------------- */

type LightboxPayload =
  | { kind: "image"; objectUrl: string }
  | { kind: "text"; body: string }
  | { kind: "decorative" };

/**
 * Lightbox sub-dialog — rendered as a SECOND Radix Dialog mounted as a
 * sibling of the captures dialog. Reuses `PanelDialogContent` so the
 * overlay (dark + 8 px backdrop blur) and entrance animation match the
 * other panel modals exactly, and uses `.modal-shell` chrome with a
 * `.modal-header` (title + `.modal-close-btn`) — no footer, since the
 * lightbox is a focused preview, not a panel-class surface.
 *
 * Radix handles click-outside-to-close via `onOpenChange`, ESC-to-close,
 * and focus trap; we don't need to wire those ourselves.
 *
 * The body switches on `payload.kind`:
 *   - `image` — full-bleed `<img>` with `object-fit: contain` inside a
 *     dark frame (matches `UploadLightbox` image branch in
 *     `AssetLocker.tsx`).
 *   - `text` — scrollable preformatted block for markdown chat
 *     exports (same chrome as `UploadLightbox` text branch).
 *   - `decorative` — the original tinted-thumb + caption layout used
 *     by the seed entries.
 */
const CAPTURE_EMPTY_TAGS: readonly string[] = Object.freeze([]);

/** Inline tag editor for one user capture (CC-TAGGING). Reads live tags from
 *  `useCaptures` and writes via its IDB-only `update()` so a tag edit never
 *  round-trips the GitHub-sync path. */
function CaptureTagBar({
  workshopId,
  captureId,
  title,
}: {
  workshopId: string;
  captureId: string;
  title: string;
}) {
  const wsId = workshopId as Workshop["id"];
  const tags = useCaptures(
    (s) =>
      s.entriesByWorkshop[wsId]?.find((c) => c.id === captureId)?.tags ??
      (CAPTURE_EMPTY_TAGS as string[]),
  );
  const update = useCaptures((s) => s.update);
  return (
    <div
      className="mt-3 flex items-center gap-2"
      style={{ borderTop: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)", paddingTop: 10 }}
    >
      <span
        style={{
          fontSize: "var(--fs-micro)",
          color: "var(--text-muted)",
          letterSpacing: 1,
          textTransform: "uppercase",
          flex: "none",
        }}
      >
        Tags
      </span>
      <TagInput
        value={tags}
        onChange={(next) => update(wsId, captureId, { tags: next })}
        workshopId={wsId}
        ariaLabel={`Tags for ${title}`}
        className="flex-1"
      />
    </div>
  );
}

function LightboxDialog({
  capture,
  payload,
  open,
  workshopId,
  onOpenChange,
}: {
  capture: GridCapture | null;
  payload: LightboxPayload;
  open: boolean;
  workshopId: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width:
            payload.kind === "decorative"
              ? "min(calc(var(--os-vw) * 100 - 48px), 720px)"
              : "min(calc(var(--os-vw) * 100 - 48px), 1200px)",
          maxWidth: "calc(var(--os-vw) * 100 - 48px)",
          maxHeight: "calc(var(--os-vh) * 100 - 96px)",
        }}
      >
        <DialogTitle className="sr-only">
          {capture ? capture.title : "Capture preview"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {capture
            ? `${capture.activity} · ${capture.timestamp} · ${capture.source}`
            : "Capture preview"}
        </DialogDescription>
        {capture ? (
          <div
            className="modal-shell"
            style={{ width: "100%", maxHeight: "85vh" }}
          >
            <div className="modal-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                {glyphFor(capture.kind) === "whiteboard" ? (
                  <Images
                    aria-hidden
                    style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
                  />
                ) : (
                  <Camera
                    aria-hidden
                    style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
                  />
                )}
                <span
                  className="modal-title"
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {capture.title}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="modal-close-btn"
                  aria-label="Close preview"
                >
                  <X aria-hidden style={{ width: 12, height: 12 }} />
                  CLOSE
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="dt-pane dt-pane-active">
                {payload.kind === "image" && (
                  <div
                    className="flex w-full items-center justify-center overflow-auto"
                    style={{
                      background: "var(--bg-deep)",
                      borderRadius: 10,
                      minHeight: 240,
                      maxHeight: "60vh",
                      padding: 12,
                    }}
                  >
                    <img
                      src={payload.objectUrl}
                      alt={capture.title}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                )}
                {payload.kind === "text" && (
                  <pre
                    className="console-scroll overflow-auto p-6"
                    style={{
                      background: "var(--bg-panel-alt)",
                      color: "var(--text-secondary)",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "var(--fs-base)",
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      margin: 0,
                      borderRadius: 10,
                      maxHeight: "60vh",
                    }}
                  >
                    {payload.body}
                  </pre>
                )}
                {payload.kind === "decorative" && (
                  <CaptureThumb capture={capture} enlarged />
                )}
                <div
                  className="font-mono mt-3 tracking-wider"
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "var(--fs-label)",
                  }}
                >
                  {capture.activity} · {capture.timestamp} ·{" "}
                  <span
                    style={{
                      color:
                        capture.source === "Saved"
                          ? "var(--accent-green)"
                          : "var(--accent-sky)",
                    }}
                  >
                    {capture.source}
                  </span>
                </div>
                <p
                  className="mt-2 leading-relaxed"
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-base)",
                    lineHeight: 1.6,
                  }}
                >
                  {capture.caption}
                </p>
                {/* Tags (CC-TAGGING) — user captures only. Seed/decorative
                    cards aren't store records, so there's nothing to tag. */}
                {capture.addedBy !== "seed" && (
                  <CaptureTagBar workshopId={workshopId} captureId={capture.id} title={capture.title} />
                )}
              </div>
            </div>
          </div>
        ) : null}
      </PanelDialogContent>
    </Dialog>
  );
}

/* ----------------------- Image URL cache hook --------------------- */

/**
 * Resolve each image-bearing capture's `blobKey` to an object URL on
 * demand. The cache is keyed by capture id so a re-render with the
 * same id reuses the same URL — and every URL allocated is revoked
 * when the modal unmounts (or the capture is removed from the
 * grid). Markdown / non-image captures don't go through this hook;
 * their text body is loaded on lightbox-open instead.
 *
 * We deliberately don't dedupe by `blobKey` — each capture owns its
 * URL lifecycle and a duplicate-key edge case (shouldn't happen in
 * practice) gets a fresh URL per entry. Revoke cost is negligible.
 */
function useImageUrls(captures: GridCapture[]): Map<string, string> {
  const cacheRef = useRef<Map<string, { blobKey: string; url: string }>>(
    new Map(),
  );
  const [, force] = useState(0);

  const targets = useMemo(
    () =>
      captures.filter(
        (c) =>
          c.blobKey !== undefined &&
          c.mimeType !== undefined &&
          c.mimeType.startsWith("image/"),
      ),
    [captures],
  );

  useEffect(() => {
    let cancelled = false;
    const wanted = new Set(targets.map((c) => c.id));

    // Revoke + drop URLs for captures that left the target set, or
    // whose `blobKey` changed under us (defensive — captures are
    // append-only, but seed/user merges could shift addressing).
    for (const [id, entry] of cacheRef.current) {
      const stillWanted = targets.find((c) => c.id === id);
      if (!stillWanted || stillWanted.blobKey !== entry.blobKey) {
        URL.revokeObjectURL(entry.url);
        cacheRef.current.delete(id);
      }
    }

    // Resolve any new targets lazily.
    void Promise.all(
      targets.map(async (c) => {
        if (cacheRef.current.has(c.id)) return;
        const blob = await loadBlob(c.blobKey!);
        if (cancelled) return;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        cacheRef.current.set(c.id, { blobKey: c.blobKey!, url });
        // Nudge a re-render so the grid card picks up the new URL.
        force((n) => n + 1);
      }),
    );

    return () => {
      cancelled = true;
      // Note: we don't revoke on dep-change; only on capture exit /
      // unmount. The block above handles the exit case; the cleanup
      // here is the cancel guard for the in-flight loads.
      void wanted;
    };
  }, [targets]);

  // Revoke everything on unmount.
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      for (const entry of cache.values()) {
        URL.revokeObjectURL(entry.url);
      }
      cache.clear();
    };
  }, []);

  // Project the internal cache to a flat `id → url` map for the
  // consumer. Re-derived on every render — cheap, and avoids leaking
  // the wrapper shape.
  const out = new Map<string, string>();
  for (const [id, entry] of cacheRef.current) {
    out.set(id, entry.url);
  }
  return out;
}

/* --------------------------- Component --------------------------- */

export function CapturesModal({
  open,
  onOpenChange,
  workshopId,
}: CapturesModalProps) {
  const data = getCaptures(workshopId);
  const openModal = useWorkshopModals((s) => s.open);
  const setVersionTarget = useVersionHistory((s) => s.setTarget);
  // The `workshopId` prop is loosely typed (`string`) so the modal
  // can be mounted under any scope; we narrow to the strict store
  // key on access — the store's sanitiser drops anything that isn't
  // a real workshop slot on the next hydrate, so out-of-union values
  // simply yield the empty fallback.
  const userCaptures = useCaptures(
    (s) =>
      s.entriesByWorkshop[workshopId as Workshop["id"]] ??
      (EMPTY_CAPTURES as UserCapture[]),
  );
  const removeUserCapture = useCaptures((s) => s.remove);
  const hiddenSeeds = useCaptures(
    (s) =>
      s.hiddenSeedsByWorkshop[workshopId as Workshop["id"]] ??
      (EMPTY_HIDDEN_SEEDS as string[]),
  );
  const hideSeed = useCaptures((s) => s.hideSeed);
  const resetWorkshop = useCaptures((s) => s.resetWorkshop);
  // Grid moderation — the facilitator can delete anyone's capture
  // (seeds included) and reset the grid to its authored state.
  const canManage = can(useAuth().user, "manage:captures");
  // Two-step destructive affordance: first click arms, second confirms.
  const [confirmReset, setConfirmReset] = useState(false);
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [lightboxPayload, setLightboxPayload] = useState<LightboxPayload>({
    kind: "decorative",
  });

  // Merge seed + user captures. User entries first, newest by
  // `addedAt`, then the seed ordering as authored. We compute even
  // for the "no seed bundle" branch so user captures still surface.
  const merged: GridCapture[] = useMemo(() => {
    const seedList = (data?.captures ?? [])
      .map(seedToGrid)
      .filter((s) => !hiddenSeeds.includes(s.id));
    const userList = [...userCaptures]
      .sort((a, b) => b.addedAt - a.addedAt)
      .map(userToGrid);
    return [...userList, ...seedList];
  }, [data, userCaptures, hiddenSeeds]);

  const imageUrls = useImageUrls(merged);

  const openLightbox = useCallback(async (capture: GridCapture): Promise<void> => {
    setLightboxId(capture.id);

    if (capture.blobKey && capture.mimeType?.startsWith("image/")) {
      const cached = imageUrls.get(capture.id);
      if (cached) {
        setLightboxPayload({ kind: "image", objectUrl: cached });
        return;
      }
      const blob = await loadBlob(capture.blobKey);
      if (blob) {
        const url = URL.createObjectURL(blob);
        setLightboxPayload({ kind: "image", objectUrl: url });
        return;
      }
      setLightboxPayload({ kind: "decorative" });
      return;
    }

    if (capture.mimeType === "text/markdown" && capture.url) {
      try {
        const resp = await fetch(capture.url);
        const body = await resp.text();
        setLightboxPayload({ kind: "text", body });
        return;
      } catch {
        setLightboxPayload({ kind: "decorative" });
        return;
      }
    }

    if (capture.blobKey && capture.mimeType === "text/markdown") {
      const blob = await loadBlob(capture.blobKey);
      if (blob) {
        const body = await blob.text();
        setLightboxPayload({ kind: "text", body });
        return;
      }
    }

    setLightboxPayload({ kind: "decorative" });
  }, [imageUrls]);

  // Search jump: open the matching capture's lightbox when the modal mounts.
  const [pendingJumpId, setPendingJumpId] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setPendingJumpId(null);
      return;
    }
    const pending = useJumpTarget.getState().target;
    if (
      !pending ||
      pending.workshopId !== workshopId ||
      pending.category !== "capture"
    ) {
      return;
    }
    useJumpTarget.getState().clear();
    setPendingJumpId(pending.id);
  }, [open, workshopId]);

  useEffect(() => {
    if (!open || !pendingJumpId) return;
    const capture = merged.find((c) => c.id === pendingJumpId);
    if (!capture) return;
    setPendingJumpId(null);
    void openLightbox(capture);
  }, [open, pendingJumpId, merged, openLightbox]);

  // Unknown workshop / no captures registered AND no user-added
  // entries — empty panel shell. Uses the same `.modal-shell` chrome
  // as the populated branch so the empty state reads as a first-class
  // panel rather than a bare card.
  if (!data && merged.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <PanelDialogContent
          className="max-w-5xl border-0 bg-transparent p-0 shadow-none"
          style={{ outline: "none" }}
        >
          <DialogTitle className="sr-only">Captures</DialogTitle>
          <DialogDescription className="sr-only">
            No captures registered for workshop {workshopId}.
          </DialogDescription>
          <div
            className="modal-shell"
            style={{ width: "100%", maxHeight: "85vh" }}
          >
            <div className="modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Images
                  aria-hidden
                  style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
                />
                <span className="modal-title">CAPTURES</span>
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-gold-rgb), 0.12)",
                    border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                    borderRadius: 10,
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-gold)",
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  ASSET LOCKER
                </span>
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-sky-rgb), 0.08)",
                    border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                    borderRadius: 10,
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-sky)",
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  {workshopId}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="modal-close-btn"
                  aria-label="Close"
                >
                  <X aria-hidden style={{ width: 12, height: 12 }} />
                  CLOSE
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="dt-pane dt-pane-active">
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-sub)",
                    textAlign: "center",
                    padding: "40px 0",
                  }}
                >
                  No captures registered for workshop <code>{workshopId}</code>.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <span className="modal-footer-label">XAES Phase Zero</span>
              <span className="modal-footer-sep">|</span>
              <span className="modal-footer-ws">Asset Locker — Captures</span>
            </div>
          </div>
        </PanelDialogContent>
      </Dialog>
    );
  }

  const lightboxCapture = lightboxId
    ? (merged.find((c) => c.id === lightboxId) ?? null)
    : null;

  // Re-compute the header summary to reflect the merged total — the
  // seed `data.summary` ("12 CAPTURES · …") only counts the
  // decorative set. The date comes from the most recent *live* capture
  // (`addedAt`; seeds carry 0) — the seed string's date is decorative
  // authoring and reads as wrong once real captures exist (UAT round 1:
  // "incorrect date is showing in Captures"). With no live captures we
  // keep the seed suffix; with neither, omit the date.
  const latestLive = merged.reduce((max, c) => Math.max(max, c.addedAt), 0);
  const liveDate =
    latestLive > 0
      ? new Date(latestLive)
          .toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
          .toUpperCase()
      : null;
  const summary = (() => {
    const total = merged.length;
    // Nothing visible (e.g. after a facilitator reset) — a stale seed
    // date next to "0 CAPTURES" reads as a bug.
    if (total === 0) return "0 CAPTURES";
    if (liveDate) return `${total} CAPTURES · ${liveDate}`;
    const seedSummary = data?.summary ?? "";
    const dateSuffix = seedSummary.includes("·")
      ? seedSummary.slice(seedSummary.indexOf("·") + 1).trim()
      : "";
    return dateSuffix
      ? `${total} CAPTURES · ${dateSuffix}`
      : `${total} CAPTURES`;
  })();
  // Footer mirrors the header: the seed string's frozen date is swapped
  // for the latest live capture's date (missed in the round-1 fix).
  // Suppressed entirely when the grid is empty — the authored string
  // counts seed cards that are no longer shown.
  const footerSummary =
    merged.length > 0 && data?.footerSummary
      ? withSessionDate(data.footerSummary, liveDate)
      : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setLightboxId(null);
        onOpenChange(o);
      }}
    >
      <PanelDialogContent
        className="max-w-6xl border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Captures — {workshopId}</DialogTitle>
        <DialogDescription className="sr-only">
          {summary}. {footerSummary ?? ""}.
        </DialogDescription>

        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER — legacy `.modal-header` is `space-between` flex:
              title block on the LEFT, action cluster on the right. */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Images
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">CAPTURES</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER
              </span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {workshopId}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                {summary}
              </span>
              {canManage && merged.length > 0 && (
                <button
                  type="button"
                  className="modal-close-btn"
                  aria-label={
                    confirmReset
                      ? "Confirm reset — deletes every capture, saved and premade"
                      : "Reset — clear every capture from this workshop's grid"
                  }
                  title="Deletes every capture — saved and premade — from this workshop's grid"
                  onClick={() => {
                    if (!confirmReset) {
                      setConfirmReset(true);
                      return;
                    }
                    resetWorkshop(
                      workshopId as Workshop["id"],
                      (data?.captures ?? []).map((c) => c.id),
                    );
                    setConfirmReset(false);
                  }}
                  onBlur={() => setConfirmReset(false)}
                  style={
                    confirmReset
                      ? {
                          color: "var(--accent-red)",
                          borderColor: "var(--accent-red)",
                        }
                      : undefined
                  }
                >
                  <RotateCcw aria-hidden style={{ width: 12, height: 12 }} />
                  {confirmReset ? "CONFIRM RESET" : "RESET"}
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY — legacy `.modal-body` (overflow + scrollbar styling
              come from the class). `.dt-pane.dt-pane-active` provides
              the 16 px gutter. */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              {merged.length === 0 && (
                <p
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "var(--fs-sub)",
                    textAlign: "center",
                    padding: "40px 0",
                    margin: 0,
                  }}
                >
                  No captures yet — save a board snapshot or a chat summary
                  and it lands here.
                </p>
              )}
              <div
                className="grid gap-3.5"
                style={{
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  // Cards own their height (image-sized thumbs) — don't
                  // stretch a row's cards to its tallest sibling.
                  alignItems: "start",
                }}
              >
                {merged.map((c) => (
                  <CaptureGridCard
                    key={c.id}
                    capture={c}
                    imageUrl={imageUrls.get(c.id)}
                    onSelect={() => void openLightbox(c)}
                    onRemove={
                      c.addedBy === "seed"
                        ? canManage
                          ? () =>
                              hideSeed(workshopId as Workshop["id"], c.id)
                          : undefined
                        : () =>
                            removeUserCapture(
                              workshopId as Workshop["id"],
                              c.id,
                            )
                    }
                    onHistory={
                      isVersionHistoryEnabled() && c.githubPath
                        ? () => {
                            setVersionTarget({
                              path: c.githubPath!,
                              label: c.title,
                              workshopId: workshopId as Workshop["id"],
                              kind: "capture",
                              sha: c.githubSha,
                              mimeType: c.mimeType,
                            });
                            openModal("version-history");
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER — legacy `.modal-footer` chrome. `.modal-footer-right`
              is `margin-left:auto` so the summary aligns to the right edge
              while the labels stay left-aligned. */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Asset Locker — Captures</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: "var(--accent-sky)" }}
            >
              {workshopId}
            </span>
            {footerSummary && (
              <span className="modal-footer-right">{footerSummary}</span>
            )}
          </div>
        </div>
      </PanelDialogContent>
      {/* Lightbox lives as a sibling Radix Dialog so it inherits the same
          overlay (dark + 8 px blur) and animations as the other panel
          modals, instead of an in-modal absolute overlay. */}
      <LightboxDialog
        capture={lightboxCapture}
        payload={lightboxPayload}
        open={lightboxCapture !== null}
        workshopId={workshopId}
        onOpenChange={(o) => {
          if (!o) {
            setLightboxId(null);
            setLightboxPayload({ kind: "decorative" });
          }
        }}
      />
    </Dialog>
  );
}
